import { useEffect, useMemo, useState } from "react";
import cn from "classnames";
import styles from "./index.module.scss";

const RESERVES_ANIMATION_TEMPLATE = "$00.0M";
const FLIP_INTERVAL_MS = 45;
const FLIP_DURATION_MS = 39;

function parseReservesTemplate(template) {
  const slots = [];
  let index = 0;

  while (index < template.length) {
    if (/\d/.test(template[index])) {
      slots.push({ type: "digit" });
      index += 1;
      continue;
    }

    let text = "";
    while (index < template.length && !/\d/.test(template[index])) {
      text += template[index];
      index += 1;
    }

    slots.push({ type: "static", text });
  }

  return slots;
}

function FlipDigit({ value }) {
  const [top, setTop] = useState(value);
  const [bottom, setBottom] = useState(value);
  const [isRolling, setIsRolling] = useState(false);

  useEffect(() => {
    if (value === bottom) {
      return;
    }

    setTop(bottom);
    setBottom(value);
    setIsRolling(true);
  }, [value, bottom]);

  const handleTransitionEnd = () => {
    if (!isRolling) {
      return;
    }

    setTop(bottom);
    setIsRolling(false);
  };

  return (
    <span className={styles.heroFlipDigit}>
      <span
        className={cn(styles.heroFlipRoll, isRolling && styles.heroFlipRollActive)}
        style={{ "--hero-flip-duration": `${FLIP_DURATION_MS}ms` }}
        onTransitionEnd={handleTransitionEnd}
      >
        <span aria-hidden={isRolling}>{top}</span>
        <span>{bottom}</span>
      </span>
    </span>
  );
}

export default function HeroReservesStat({ value, isSettled }) {
  const slots = useMemo(() => parseReservesTemplate(RESERVES_ANIMATION_TEMPLATE), []);
  const digitCount = useMemo(() => slots.filter((slot) => slot.type === "digit").length, [slots]);
  const [digits, setDigits] = useState(() => Array.from({ length: digitCount }, () => "0"));

  useEffect(() => {
    if (isSettled) {
      return undefined;
    }

    const intervalId = window.setInterval(() => {
      setDigits(
        Array.from({ length: digitCount }, () => String(Math.floor(Math.random() * 10)))
      );
    }, FLIP_INTERVAL_MS);

    return () => {
      window.clearInterval(intervalId);
    };
  }, [isSettled, digitCount]);

  if (isSettled) {
    return (
      <p className={`h3 ${styles.heroFlipStat}`}>
        <span className={styles.heroReservesWrap}>
          <span className={styles.heroReservesSizer} aria-hidden>
            {value}
          </span>
          <span className={styles.heroReservesSettled}>{value}</span>
        </span>
      </p>
    );
  }

  let digitIndex = 0;

  return (
    <p className={`h3 ${styles.heroFlipStat}`}>
      <span className={styles.heroReservesWrap}>
        <span className={styles.heroReservesSizer} aria-hidden>
          {RESERVES_ANIMATION_TEMPLATE}
        </span>
        <span className={styles.heroReservesFlip}>
          {slots.map((slot, index) => {
            if (slot.type === "static") {
              return (
                <span key={`static-${index}`} className={styles.heroFlipStatic}>
                  {slot.text}
                </span>
              );
            }

            const digit = digits[digitIndex];
            digitIndex += 1;

            return <FlipDigit key={`digit-${index}`} value={digit} />;
          })}
        </span>
      </span>
    </p>
  );
}
